import { Component, Input, OnChanges, AfterViewInit, ViewChild, SimpleChanges, Inject, EventEmitter, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';

import { ClassesEntity } from '../../../../../../models/entities/classes-entity';
import { IClassesEntityService } from '../../../../../../interfaces/entities/classes-entity-service.interface';
import { CLASSES_ENTITY_SERVICE_TOKEN } from '../../../../../../injection-tokens/entity-service-tokens';
import { Log } from '../../../../../../services/logger.service';
import { DEFAULT_PAGINATOR_PAGE_SIZE, PAGINATOR_PAGE_SIZE_OPTIONS } from '../../../../../../shared/pagination-options';
import {
  VmSubscriptionAddDialogComponent,
  VmSubscriptionAddDialogResult,
} from './vm-subscription-add-dialog.component';

export interface VmSubRow {
  subscriptionId: string;
  displayName: string;
  location: string;
  count: number;
  coresLimit?: number;
  coresCurrentUsage?: number;
  pendingCores?: number;
  quotaCheckedAt?: string;
  /** Maximum additional VMs provisionable given remaining core budget. */
  maxVms?: number;
  /** vCPU count per VM for the class VM SKU. */
  vmVCpus?: number;
  /** True when the cached quota is older than the staleness threshold. */
  isStale?: boolean;
  isRefreshing?: boolean;
  isRemoving?: boolean;
  /** Per-family core quota from the Azure usage API. Key = VM family name. */
  familyQuotaMap?: Record<string, { limit: number; usage: number }>;
  /** 'registering' while Azure resource providers are being registered for this subscription;
   *  'registered' once registration is complete and quota is available. */
  providerRegistrationStatus?: string;
}

/** Shared 5-band colour palette for VM badge and quota bar. */
const BAND_COLORS = {
  green: '#43a047',
  yellow: '#fbc02d',
  orange: '#fb8c00',
  red: '#e53935',
  grey: '#9e9e9e',
} as const;

@Component({
  selector: 'app-vm-subscription-settings',
  templateUrl: './vm-subscription-settings.component.html',
  styleUrls: ['./vm-subscription-settings.component.css'],
})
export class VmSubscriptionSettingsComponent implements OnChanges, AfterViewInit {
  @Input() classEntity!: ClassesEntity;
  @Input() classId!: string;

  /** Emitted when subscriptions are added or removed so the parent can re-fetch classEntity
   * and lift the VM-template creation guard. */
  @Output() subscriptionsChanged = new EventEmitter<void>();
  /** Emitted when the user clicks the deep-link in the tenant-gate banner. */
  @Output() goToTenant = new EventEmitter<void>();
  /**
   * Emitted whenever the total provisionable-VM count changes.
   * null  = quota is currently loading (parent should treat as unconstrained).
   * number = sum of maxVms across all subscriptions (may be 0 if quota is exhausted).
   * This is REMAINING capacity (limit − current usage); it is not the participant cap.
   */
  @Output() vmCapacityChanged = new EventEmitter<number | null>();
  /**
   * Emitted whenever the GROSS VM slot count changes — the total number of class VMs the
   * subscriptions can ever hold, independent of current Azure core usage. This is the number
   * the participant capacity gate is derived from.
   *   grossPerSub = min( floor(coresLimit / vCpu),
   *                      familyLimit > 0 ? floor(familyLimit / vCpu) : ∞ )
   * null = quota is currently loading.
   */
  @Output() vmGrossCapacityChanged = new EventEmitter<number | null>();
  /** Emitted with true when any contributing subscription's quota cache is stale. */
  @Output() vmQuotaStaleChanged = new EventEmitter<boolean>();

  @ViewChild(MatPaginator) paginator!: MatPaginator;

  dataSource = new MatTableDataSource<VmSubRow>([]);
  quotaMap: Record<string, any> = {};
  isLoadingQuota = false;
  isRefreshingAll = false;

  displayedColumns = ['displayName', 'subscriptionId', 'location', 'optionalVms', 'quota', 'actions'];
  readonly pageSizeOptions = PAGINATOR_PAGE_SIZE_OPTIONS;
  readonly pageSize = DEFAULT_PAGINATOR_PAGE_SIZE;

  private readonly logger = Log.create(VmSubscriptionSettingsComponent);

  constructor(
    @Inject(CLASSES_ENTITY_SERVICE_TOKEN) private readonly classService: IClassesEntityService,
    private readonly dialog: MatDialog,
    private readonly snackBar: MatSnackBar,
  ) { }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
  }

  ngOnChanges(_: SimpleChanges): void {
    this.rebuildRows();
    this.loadQuota();
    this.loadDependentResourceData();
  }

  /** Convenience accessor kept for backward compat in template. */
  get subscriptions(): VmSubRow[] {
    return this.dataSource.data;
  }

  private setRows(rows: VmSubRow[]): void {
    this.dataSource.data = rows;
    this.emitVmCapacity();
  }

  /**
   * Emits the current total provisionable-VM count.
   * Emits null while the initial quota load is in-flight so the participant gate
   * stays in the 'loading' state rather than incorrectly blocking adds.
   */
  private emitVmCapacity(): void {
    if (this.isLoadingQuota) {
      this.vmCapacityChanged.emit(null);
      this.vmGrossCapacityChanged.emit(null);
      return;
    }
    const rows = this.dataSource.data;
    const total = rows.reduce((sum, r) => sum + (r.maxVms ?? 0), 0);
    const gross = rows.reduce((sum, r) => sum + this.grossVmsForRow(r), 0);
    this.vmCapacityChanged.emit(total);
    this.vmGrossCapacityChanged.emit(gross);
    this.vmQuotaStaleChanged.emit(rows.some(r => r.isStale === true));
  }

  /**
   * Gross class-VM slots one subscription can ever hold, ignoring current usage.
   * The stricter of the total-core ceiling and the per-VM-family ceiling governs.
   * Returns 0 when the SKU vCPU count or the core limit is not yet known.
   */
  private grossVmsForRow(r: VmSubRow): number {
    const vcpu = r.vmVCpus ?? 0;
    const coresLimit = r.coresLimit ?? 0;
    if (vcpu <= 0 || coresLimit <= 0) return 0;

    let slots = Math.floor(coresLimit / vcpu);

    const familyLimits = Object.values(r.familyQuotaMap ?? {})
      .map(e => e?.limit ?? 0)
      .filter(l => l > 0);
    if (familyLimits.length) {
      slots = Math.min(slots, Math.floor(Math.min(...familyLimits) / vcpu));
    }
    return Math.max(0, slots);
  }

  private rebuildRows(): void {
    const raw: any[] = this.classEntity?.data?.['VMSubscription']
      ?? this.classEntity?.data?.['VmSubscription']
      ?? [];
    this.setRows(raw.map(r => ({
      subscriptionId: r.subscriptionId ?? r.SubscriptionId ?? '',
      displayName: r.displayName ?? r.DisplayName ?? '',
      location: r.location ?? r.Location ?? '',
      count: r.Count ?? r.count ?? 0,
    })));
  }

  /** Reads quota from the DB cache (fast, called on initial load). Stale entries are then
   * silently refreshed in the background via live Azure calls. */
  loadQuota(): void {
    if (!this.classId) return;
    this.isLoadingQuota = true;
    // Immediately signal the loading state so the parent enters the 'calculating'
    // phase rather than seeing stale 0 from the rebuildRows() call that preceded this.
    this.vmCapacityChanged.emit(null);
    this.vmGrossCapacityChanged.emit(null);
    this.classService.getSubscriptionQuotaSummary(this.classId).subscribe({
      next: (data: any[]) => {
        this.isLoadingQuota = false;
        this.quotaMap = {};
        (data ?? []).forEach(q => {
          const id = (q.subscriptionId ?? q.SubscriptionId ?? '').toLowerCase();
          if (id) this.quotaMap[id] = q;
        });
        this.mergeQuotaIntoRows();
        // Silently refresh any stale entries in the background so the UI self-heals
        // without the user needing to manually trigger a per-row refresh.
        this.backgroundRefreshStaleRows();
      },
      error: () => { this.isLoadingQuota = false; },
    });
  }

  /** Calls the live Azure quota endpoint for every current row in parallel.
   * Triggered by the "Refresh All" button to guarantee fresh data from Azure. */
  refreshAllQuota(): void {
    if (!this.classId || this.isRefreshingAll) return;
    const rows = this.dataSource.data;
    if (!rows.length) return;

    this.isRefreshingAll = true;
    rows.forEach(r => (r.isRefreshing = true));
    this.setRows([...rows]);

    let completed = 0;
    const total = rows.length;
    const done = (): void => {
      completed++;
      if (completed >= total) {
        this.isRefreshingAll = false;
        this.snackBar.open('All quotas refreshed', 'Dismiss', { duration: 2500 });
        // Re-sync the participant-VM/template-VM portion of the delete lock too, so a manual
        // refresh fully re-evaluates blockReason() rather than just the pending-cores portion.
        this.loadDependentResourceData();
      }
    };

    rows.forEach(row => {
      this.classService.refreshClassVmSubscriptionQuota(this.classId, row.subscriptionId).subscribe({
        next: (res: any) => {
          this.applyQuotaResponseToRow(row, res);
          row.isRefreshing = false;
          this.setRows([...this.dataSource.data]);
          done();
        },
        error: () => {
          row.isRefreshing = false;
          this.setRows([...this.dataSource.data]);
          done();
        },
      });
    });
  }

  /** Silently (no spinner, no snackbar) refreshes rows where isStale === true. */
  private backgroundRefreshStaleRows(): void {
    const staleRows = this.dataSource.data.filter(r => r.isStale);
    staleRows.forEach(row => {
      this.classService.refreshClassVmSubscriptionQuota(this.classId, row.subscriptionId).subscribe({
        next: (res: any) => {
          this.applyQuotaResponseToRow(row, res);
          this.setRows([...this.dataSource.data]);
        },
        error: () => { /* silent — stale data remains, user can manually refresh */ },
      });
    });
  }

  private mergeQuotaIntoRows(): void {
    this.setRows(this.dataSource.data.map(row => {
      const q = this.quotaMap[row.subscriptionId.toLowerCase()];
      const rawMap = q?.familyQuotaMap ?? q?.FamilyQuotaMap;
      let familyQuotaMap: Record<string, { limit: number; usage: number }> | undefined;
      if (rawMap) {
        familyQuotaMap = this.normalizeFamilyQuotaMap(rawMap);
      } else {
        // GetSubscriptionQuotaSummary returns scalar family fields rather than a full map.
        // Synthesise a single-entry map so the tooltip is shown.
        const familyName = q?.vmFamilyName ?? q?.VmFamilyName;
        const familyLimit = q?.familyCoresLimit ?? q?.FamilyCoresLimit;
        const familyUsage = q?.familyCoresUsage ?? q?.FamilyCoresUsage ?? 0;
        if (familyName && familyLimit != null) {
          familyQuotaMap = { [familyName]: { limit: familyLimit, usage: familyUsage } };
        }
      }
      const coresLimit = q?.coresLimit ?? q?.CoresLimit;
      const prs = q?.providerRegistrationStatus ?? q?.ProviderRegistrationStatus
        ?? (coresLimit == null ? 'registering' : 'registered');
      return {
        ...row,
        coresLimit,
        coresCurrentUsage: q?.coresCurrentUsage ?? q?.CoresCurrentUsage,
        pendingCores: q?.pendingCores ?? q?.PendingCores,
        quotaCheckedAt: q?.quotaCheckedAt ?? q?.QuotaCheckedAt,
        maxVms: q?.maxVms ?? q?.MaxVms,
        vmVCpus: q?.vmVCpus ?? q?.VmVCpus,
        // Treat a missing quota record (brand-new subscription, no cache yet) as stale so
        // backgroundRefreshStaleRows() auto-populates quota data without manual refresh.
        isStale: q == null || (q?.isStale ?? q?.IsStale ?? false),
        familyQuotaMap,
        providerRegistrationStatus: prs,
      };
    }));
  }

  /** Normalises a raw API family-quota map (handles both PascalCase and camelCase keys). */
  private normalizeFamilyQuotaMap(
    raw: Record<string, any>,
  ): Record<string, { limit: number; usage: number }> {
    const result: Record<string, { limit: number; usage: number }> = {};
    for (const [family, entry] of Object.entries(raw)) {
      result[family] = {
        limit: entry?.limit ?? entry?.Limit ?? 0,
        usage: entry?.usage ?? entry?.Usage ?? 0,
      };
    }
    return result;
  }

  /** Merges a refresh-quota API response into a row object.
   * Guards against overwriting valid existing values with undefined — only updates
   * a field when the response actually provides a non-undefined value. */
  private applyQuotaResponseToRow(row: VmSubRow, res: any): void {
    if (res?.coresLimit !== undefined) row.coresLimit = res.coresLimit;
    else if (res?.CoresLimit !== undefined) row.coresLimit = res.CoresLimit;

    if (res?.coresCurrentUsage !== undefined) row.coresCurrentUsage = res.coresCurrentUsage;
    else if (res?.CoresCurrentUsage !== undefined) row.coresCurrentUsage = res.CoresCurrentUsage;

    if (res?.pendingCores !== undefined) row.pendingCores = res.pendingCores;
    else if (res?.PendingCores !== undefined) row.pendingCores = res.PendingCores;

    if (res?.quotaCheckedAt !== undefined) row.quotaCheckedAt = res.quotaCheckedAt;
    else if (res?.QuotaCheckedAt !== undefined) row.quotaCheckedAt = res.QuotaCheckedAt;

    if (res?.maxVms !== undefined) row.maxVms = res.maxVms;
    else if (res?.MaxVms !== undefined) row.maxVms = res.MaxVms;

    if (res?.vmVCpus !== undefined) row.vmVCpus = res.vmVCpus;
    else if (res?.VmVCpus !== undefined) row.vmVCpus = res.VmVCpus;

    const rawMap = res?.familyQuotaMap ?? res?.FamilyQuotaMap;
    if (rawMap !== undefined) row.familyQuotaMap = this.normalizeFamilyQuotaMap(rawMap);

    const prs = res?.providerRegistrationStatus ?? res?.ProviderRegistrationStatus;
    if (prs !== undefined) row.providerRegistrationStatus = prs;

    row.isStale = false;
  }

  /** True when the class has Azure SP credentials stored (Data.IsSpConfigured === true). */
  get isTenantConfigured(): boolean {
    return !!this.classEntity?.data?.['IsSpConfigured'];
  }

  // ---- Proactive delete-lock (mirrors RemoveClassVmSubscription backend guards) ------------

  /** Count of active participant VMs per subscription, keyed by lowercased subscriptionId. */
  private activeVmCountBySub: Record<string, number> = {};
  /** Subscription ID the class template VM is currently deployed in (null if none/destroyed). */
  private templateVmSubscriptionId: string | null = null;

  /**
   * Loads the same per-class resource data the Resources tab uses (no new endpoint) to
   * proactively compute which subscriptions have dependent User VM / Template VM resources,
   * so the delete button can be disabled before the user clicks it. Best-effort: on failure
   * the buttons simply fall back to relying on the backend's authoritative guard + error.
   */
  private loadDependentResourceData(): void {
    if (!this.classId) return;
    this.classService.getClassResources(this.classId).subscribe({
      next: (data: any) => {
        const vms: any[] = data?.userVMs ?? data?.UserVMs ?? [];
        const counts: Record<string, number> = {};
        for (const vm of vms) {
          const subId = (vm.subscriptionId ?? vm.SubscriptionId ?? '').toLowerCase();
          if (!subId) continue;
          counts[subId] = (counts[subId] ?? 0) + 1;
        }
        this.activeVmCountBySub = counts;

        const templates: any[] = data?.templates ?? data?.Templates ?? [];
        const activeTemplate = templates.find(t => {
          const status = (t.customerStatus ?? t.CustomerStatus ?? '').toLowerCase();
          return !!status && !['draft', 'failed', 'destroy-failed'].includes(status);
        });
        this.templateVmSubscriptionId = activeTemplate
          ? (activeTemplate.subscriptionId ?? activeTemplate.SubscriptionId ?? null)
          : null;

        this.setRows([...this.dataSource.data]); // re-render disabled states with fresh data
      },
      error: () => { /* silent — proactive disable is best-effort; backend guard remains authoritative */ },
    });
  }

  /**
   * Mirrors the backend guard in `RemoveClassVmSubscription`. Returns a human-readable reason
   * when the row's delete button should be disabled, or null when removal is allowed.
   */
  blockReason(row: VmSubRow): string | null {
    const activeVms = this.activeVmCountBySub[row.subscriptionId.toLowerCase()] ?? 0;
    if (activeVms > 0) {
      return `Cannot remove: ${activeVms} participant VM(s) are still active in this subscription.`;
    }
    if ((row.pendingCores ?? 0) > 0) {
      return `Cannot remove: ${row.pendingCores} core(s) are reserved for an in-progress VM creation in this subscription.`;
    }
    if ((row.coresCurrentUsage ?? 0) > 0) {
      return `Cannot remove: ${row.coresCurrentUsage} core(s) are currently in use in this subscription according to Azure.`;
    }
    if (this.templateVmSubscriptionId
      && this.templateVmSubscriptionId.toLowerCase() === row.subscriptionId.toLowerCase()) {
      return 'Cannot remove: the class template VM is still deployed in this subscription.';
    }
    return null;
  }

  openAddDialog(): void {
    const ref = this.dialog.open(VmSubscriptionAddDialogComponent, {
      width: '600px',
      maxWidth: '95vw',
      disableClose: false,
      data: {
        classId: this.classId,
        existingSubIds: this.subscriptions.map(s => s.subscriptionId),
        existingVmSubscriptions: this.subscriptions.map(s => ({
          subscriptionId: s.subscriptionId,
          location: s.location,
        })),
      },
    });

    ref.afterClosed().subscribe((result: VmSubscriptionAddDialogResult | null) => {
      if (!result?.added?.length) return;
      const newRows: VmSubRow[] = result.added
        .filter(a => !this.dataSource.data.some(s =>
          s.subscriptionId?.toLowerCase() === a.subscriptionId?.toLowerCase()
          && (s.location ?? '').trim().toLowerCase().replace(/\s+/g, '')
          === (a.location ?? '').trim().toLowerCase().replace(/\s+/g, '')))
        .map(a => ({
          subscriptionId: a.subscriptionId,
          displayName: a.displayName,
          location: a.location,
          count: 0,
          coresLimit: a.coresLimit,
          coresCurrentUsage: a.coresCurrentUsage,
          pendingCores: a.pendingCores,
          quotaCheckedAt: a.quotaCheckedAt,
          providerRegistrationStatus: a.providerRegistrationStatus,
        }));
      this.setRows([...this.dataSource.data, ...newRows]);
      if (newRows.length) {
        this.snackBar.open(
          `${newRows.length} subscription${newRows.length > 1 ? 's' : ''} added`,
          'Dismiss',
          { duration: 3000, panelClass: 'snack-success' },
        );
        this.subscriptionsChanged.emit();
      }
    });
  }

  removeSubscription(row: VmSubRow): void {
    row.isRemoving = true;
    this.classService.removeClassVmSubscription(this.classId, row.subscriptionId).subscribe({
      next: () => {
        this.setRows(this.dataSource.data.filter(s => s.subscriptionId !== row.subscriptionId));
        this.snackBar.open('Subscription removed', 'Dismiss', { duration: 2500, panelClass: 'snack-success' });
        this.subscriptionsChanged.emit();
      },
      error: (err: any) => {
        row.isRemoving = false;
        const msg = err?.error?.message ?? 'Failed to remove subscription';
        this.snackBar.open(msg, 'Dismiss', { duration: 4000, panelClass: 'snack-error' });
      },
    });
  }

  refreshQuota(row: VmSubRow): void {
    row.isRefreshing = true;
    this.classService.refreshClassVmSubscriptionQuota(this.classId, row.subscriptionId).subscribe({
      next: (res: any) => {
        row.isRefreshing = false;
        this.applyQuotaResponseToRow(row, res);
        // Re-render the table so computed columns reflect the new values
        this.setRows([...this.dataSource.data]);
        this.snackBar.open('Quota refreshed', 'Dismiss', { duration: 2000 });
        // Re-sync the participant-VM/template-VM portion of the delete lock too, so a manual
        // refresh fully re-evaluates blockReason() rather than just the pending-cores portion.
        this.loadDependentResourceData();
      },
      error: (err: any) => {
        row.isRefreshing = false;
        this.snackBar.open(err?.error?.message ?? 'Quota refresh failed', 'Dismiss', { duration: 3000, panelClass: 'snack-error' });
      },
    });
  }

  /** Returns a multi-line tooltip string listing per-family usage, or empty string when unavailable. */
  getFamilyTooltip(row: VmSubRow): string {
    if (!row.familyQuotaMap) return '';
    const entries = Object.entries(row.familyQuotaMap);
    if (!entries.length) return '';
    return entries
      .map(([family, q]) => `${family}: ${q.usage}/${q.limit} cores`)
      .join('\n');
  }

  quotaUsagePercent(row: VmSubRow): number {
    const limit = row.coresLimit ?? 0;
    const used = row.coresCurrentUsage ?? 0;
    return limit > 0 ? Math.round((used / limit) * 100) : 0;
  }

  /**
   * Returns the MDC progress-bar fill colour based on REMAINING quota capacity.
   * Bands (available fraction):
   *   ≥75% remaining → green  | ≥50% → yellow | ≥25% → orange | >0% → red | 0 / no data → grey
   */
  quotaBarColor(row: VmSubRow): string {
    const limit = row.coresLimit ?? 0;
    const used = row.coresCurrentUsage ?? 0;
    if (limit <= 0) return BAND_COLORS.grey;
    const avail = (limit - used) / limit;
    if (avail <= 0) return BAND_COLORS.grey;
    if (avail < 0.25) return BAND_COLORS.red;
    if (avail < 0.50) return BAND_COLORS.orange;
    if (avail < 0.75) return BAND_COLORS.yellow;
    return BAND_COLORS.green;
  }

  /**
   * Returns a CSS class for the "VMs Available" badge.
   * Bands based on maxVms / totalPossibleVms (coresLimit / vmVCpus).
   * Falls back to remaining quota fraction when total is unavailable.
   */
  vmBadgeBand(row: VmSubRow): string {
    const maxVms = row.maxVms ?? 0;
    if (maxVms <= 0) return 'band-grey';

    const total = (row.coresLimit ?? 0) > 0 && (row.vmVCpus ?? 0) > 0
      ? Math.floor(row.coresLimit! / row.vmVCpus!)
      : 0;

    const frac = total > 0
      ? maxVms / total
      : (() => {
        const limit = row.coresLimit ?? 0;
        const used = row.coresCurrentUsage ?? 0;
        return limit > 0 ? (limit - used) / limit : -1;
      })();

    if (frac < 0) return 'band-grey';
    if (frac < 0.25) return 'band-red';
    if (frac < 0.50) return 'band-orange';
    if (frac < 0.75) return 'band-yellow';
    return 'band-green';
  }

  formatQuotaCheckedAt(ts: string | undefined): string {
    return ts ? new Date(ts).toLocaleString() : '—';
  }
}
